import { MicrophoneIcon } from '@heroicons/react/outline';
import { MicrophoneIcon as MicrophoneIconSolid } from '@heroicons/react/solid';
import useLocale from 'next-translate/useTranslation';
import { memo, useState } from 'react';
import { toast } from 'sonner';

const AudioRecorder = dynamic(() => import('@/components/Chat/AudioRecorder'), {
  ssr: false,
});

import dynamic from 'next/dynamic';

import { MAX_DICTATION_SECONDS, transcribeDictation } from '@/lib/dictation';

import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';

import Modal from '../atoms/Modal';

interface MicrophoneProps {
  setTranscription: (text: string) => void;
}

const Microphone = ({ setTranscription }: MicrophoneProps) => {
  const { t } = useLocale('tools');
  const { t: tCommon } = useLocale('common');
  const [recording, setRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const analytics = useAnalytics();
  const [showMicrophone, setShowMicrophone] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();

  if (!showMicrophone) {
    return null;
  }

  const handleAudioReady = async (audio: Blob) => {
    setIsTranscribing(true);
    try {
      const { text, sizeInMB } = await transcribeDictation(audio, {
        userId: user?.id,
      });
      setTranscription(text);

      analytics?.track('Audio Transcribed', {
        transcription: text,
        page: window.location.pathname,
        size: sizeInMB,
      });
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error(tCommon('Failed to transcribe audio. Please try again.'));
    } finally {
      setIsTranscribing(false);
      setRecording(false);
      setShowModal(false);
    }
  };

  return (
    <>
      <Modal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        className='p-20'
      >
        <AudioRecorder
          onAudioAvailable={handleAudioReady}
          isLoading={isTranscribing}
          maxRecordDuration={MAX_DICTATION_SECONDS}
          startRecordingImmediately={true}
        />
      </Modal>
      {!recording ? (
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          className='group ml-2 flex items-start justify-start space-x-2 text-gray-500 hover:text-red-500'
        >
          <MicrophoneIcon className='h-5 w-5 rounded-md disabled:hover:bg-transparent dark:hover:text-gray-400 dark:disabled:hover:bg-transparent' />
          {t('Dictate')}
        </button>
      ) : (
        <button className='' onClick={() => setShowModal(false)}>
          <span className='relative'>
            <span className='absolute left-1 inline-flex h-4 w-4 animate-ping rounded-full bg-red-400 opacity-75'></span>
          </span>
          <MicrophoneIconSolid className='h-5 w-5 rounded-md text-gray-500 hover:bg-gray-100 disabled:hover:bg-transparent dark:hover:bg-gray-900 dark:hover:text-gray-400 dark:disabled:hover:bg-transparent' />
        </button>
      )}
    </>
  );
};

export default memo(Microphone);
